import { FormattedMessage, useIntl } from "react-intl";
import { labelForCondition } from "@/components/match/match-result-derive";
import { useConditionLabelFormatters, useConditionLabelLookup } from "@/components/match/result-panel-labels";
import { criterionFormatter } from "@/components/match/company-ranking-derive";
import { ELEMENT_CODES } from "@/lib/elements";
import { powerName } from "@/lib/power-library";
import { POWER_SCORE_LENS_LABEL_IDS, MISSING_POWER_LABEL_IDS } from "@/lib/score-source";
import { POWERS_WEIGHT_DISPLAY_DIVISOR } from "@/components/match/match-design-rules";
import type { ListingRequirementDifference } from "./listing-requirements";

const SETTINGS: Record<string, { label: string; values: Record<string, string> }> = {
  candidateAssessmentDetail: { label: "match.challenge.assessmentDetail", values: {
    BULLETS: "match.challenge.assessmentDetailBullets", SENTENCES: "match.challenge.assessmentDetailSentences", LONG_TEXT: "match.challenge.assessmentDetailLongText",
  } },
  candidateAssessmentStrictness: { label: "match.challenge.assessmentStrictness", values: {
    NORMAL: "match.challenge.assessmentStrictnessNormal", STRICT: "match.challenge.assessmentStrictnessStrict", EXTREME: "match.challenge.assessmentStrictnessExtreme",
  } },
  candidateAssessmentOutcomeFocus: { label: "match.challenge.assessmentOutcomeFocus", values: {
    BALANCED: "match.challenge.assessmentOutcomeBalanced", RISKS: "match.challenge.assessmentOutcomeRisks", OPPORTUNITIES: "match.challenge.assessmentOutcomeOpportunities",
  } },
  requirementLeniency: { label: "match.challenge.requirementLeniency", values: {
    LENIENT: "match.challenge.requirementLeniencyLenient", MUST_STRICT: "match.challenge.requirementLeniencyMustStrict", STRICT: "match.challenge.requirementLeniencyStrict",
  } },
  powerScoreLens: { label: "match.powerScore.source", values: POWER_SCORE_LENS_LABEL_IDS },
  missingPowerScore: { label: "match.powerScore.missing", values: MISSING_POWER_LABEL_IDS },
};

export function ListingRequirementsDiff({ differences }: { differences: readonly ListingRequirementDifference[] }) {
  const intl = useIntl();
  const lookup = useConditionLabelLookup();
  const formatters = useConditionLabelFormatters(intl);
  const company = criterionFormatter(intl, lookup.industries);
  const message = (id: string) => intl.formatMessage({ id });
  const conditionLabel = (row: Record<string, unknown>) => String(row.__typename).startsWith("Company")
    ? company.requirement(row) : labelForCondition(row, lookup, formatters);
  function describe(diff: ListingRequirementDifference): { label: string; before: string; after: string } {
    if (diff.kind === "condition") {
      const before = diff.before as Record<string, unknown> | undefined;
      const after = diff.after as Record<string, unknown> | undefined;
      const oldLabel = before ? conditionLabel(before) : message("share.listings.versions.diff.absent");
      const newLabel = after ? conditionLabel(after) : message("share.listings.versions.diff.absent");
      const sameLabel = !!before && !!after && oldLabel === newLabel;
      function details(row: Record<string, unknown> | undefined, label: string) {
        if (!row) return label;
        const parts = sameLabel ? [] : [label];
        if (before?.requirement !== after?.requirement) parts.push(message(row.requirement === "MUST"
          ? "match.result.ranking.must" : "match.result.ranking.optional"));
        if (before?.active !== after?.active) parts.push(message(row.active === false
          ? "match.pdf.inactive" : "share.listings.versions.diff.active"));
        if (before?.aiMode !== after?.aiMode && row.aiMode != null) parts.push(intl.formatMessage({ id: "share.listings.versions.diff.ai" }, {
          value: message(row.aiMode === "NO_AI" ? "share.listings.versions.diff.off" : "share.listings.versions.diff.on"),
        }));
        return parts.join(" · ") || label;
      }
      return { label: sameLabel ? oldLabel : message("match.challenge.requirements"),
        before: details(before, oldLabel), after: details(after, newLabel) };
    }
    if (diff.kind === "power") {
      const name = powerName(intl, ELEMENT_CODES[diff.key] ?? diff.key);
      const weight = (value: unknown) => typeof value === "number" ? intl.formatNumber(value / POWERS_WEIGHT_DISPLAY_DIVISOR) : "–";
      return { label: intl.formatMessage({ id: "share.listings.versions.diff.power" }, { name }),
        before: weight(diff.before), after: weight(diff.after) };
    }
    const setting = SETTINGS[diff.key];
    const value = (raw: unknown) => {
      const id = setting?.values[String(raw)];
      return id ? message(id) : "–";
    };
    return { label: setting ? message(setting.label) : message("match.challenge.requirements"),
      before: value(diff.before), after: value(diff.after) };
  }
  return <span className="block space-y-2">
    <span className="block font-medium"><FormattedMessage id="share.listings.versions.diff.direction" /></span>
    <span role="list" className="block max-h-64 space-y-2 overflow-y-auto">
      {differences.map((diff, index) => {
        const detail = describe(diff);
        return <span key={`${diff.kind}-${diff.key}-${index}`} role="listitem" className="block space-y-1">
          <span className="block font-medium">{detail.label}</span>
          <span className="block"><FormattedMessage id="share.listings.versions.diff.before" />: {detail.before}</span>
          <span className="block"><FormattedMessage id="share.listings.versions.diff.after" />: {detail.after}</span>
        </span>;
      })}
    </span>
  </span>;
}
